import { Box, Divider, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { INotification } from 'store/ticket-service/types';

import { NamespacesList } from 'core/enums/common';

import { IFormattedNotification } from 'components/TicketSteps/NotificationStep';
import { CustomSwitch } from 'components/shared/CustomSwitch';

import { regularTextColor, titleTextColor } from 'assets/styles/_palette';

export const NotificationsChangesDisplayBlock = ({
	oldValue,
	newValue,
}: {
	oldValue: Record<string, any>;
	newValue: Record<string, any>;
}) => {
	const { t: notificationsTranslate } = useTranslation(NamespacesList['notificationsStep']);

	const getUpdatedValueStyles = ({
		oldValue,
		newValue,
		isDeleted,
		showStyles,
	}: {
		oldValue: string;
		newValue: string;
		isDeleted: boolean;
		showStyles: boolean;
	}) => {
		if (showStyles) {
			if (isDeleted) {
				return { backgroundColor: '#FB0B244C' };
			}

			if (oldValue.trim() !== newValue.trim()) {
				return { backgroundColor: '#01C8604C' };
			}
		}

		return {};
	};

	const getNotificationsBlock = ({
		newValue,
		oldValue,
		isNewBlock,
	}: {
		oldValue: Record<string, any>;
		newValue: Record<string, any>;
		isNewBlock: boolean;
	}) => {
		const usedValue = isNewBlock ? newValue : oldValue;

		const parsedValue = usedValue.notifications.map((notification: INotification) => ({
			...notification,
			permissions: {
				Email: notification.email,
				SMS: notification.sms,
				Push: notification.push,
			},
		}));

		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: { md: '16px', xl: '24px' },
					width: '100%',
					padding: '12px 16px',
					borderRadius: '8px',
					overflow: 'auto',
					backgroundColor: '#F3F4F580',
				}}
			>
				{parsedValue.map((notification: IFormattedNotification, index: number) => (
					<Box
						key={index}
						sx={{
							width: { xs: '100%', md: 'unset' },
							marginTop: { xs: '16px', md: '0' },
						}}
					>
						<Box
							className="rtl-able"
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								gap: '12px',
								marginBottom: { xs: '12px', xl: '24px' },
							}}
						>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'space-between',
								}}
							>
								<Typography
									className="rtl-able"
									sx={{
										fontWeight: '700',
										color: titleTextColor,
										...getUpdatedValueStyles({
											oldValue: oldValue.notifications[index].title,
											newValue: newValue.notifications[index].title,
											isDeleted: parsedValue[index].isDeleted,
											showStyles: isNewBlock,
										}),
									}}
								>
									{notification.title}
								</Typography>
								<Typography
									className="rtl-able"
									sx={{
										color: regularTextColor,
										...getUpdatedValueStyles({
											oldValue: oldValue.notifications[index].description,
											newValue: newValue.notifications[index].description,
											isDeleted: parsedValue[index].isDeleted,
											showStyles: isNewBlock,
										}),
									}}
								>
									{notification.description}
								</Typography>
							</Box>

							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'center',
									gap: { xs: '10px', xl: '16px' },
								}}
							>
								{Object.entries(notification.permissions).map(
									([permission, value]: [string, boolean], permissionIndex) => (
										<Box
											className="rtl-able"
											key={`${index * 3 + permissionIndex}${permission}`}
											sx={{
												width: '100%',
												display: 'flex',
												gap: '8px',
												marginRight: '5px',
												justifyContent: 'space-between',
											}}
										>
											<CustomSwitch
												id={`${index * 3 + permissionIndex}${permission}`}
												checked={value}
											/>
											<Typography
												className="rtl-able"
												sx={{
													fontSize: '14px',
													fontWeight: '700',
													whiteSpace: 'nowrap',
													color: titleTextColor,
												}}
											>
												{notificationsTranslate(`notifications.${permission}`)}
											</Typography>
										</Box>
									)
								)}
							</Box>
						</Box>

						{parsedValue.length - 1 !== index && <Divider sx={{ marginRight: '5px' }} />}
					</Box>
				))}
			</Box>
		);
	};

	return (
		<Box sx={{ display: 'flex', maxHeight: '90%', gap: '24px' }}>
			{getNotificationsBlock({ newValue, oldValue, isNewBlock: false })}
			{getNotificationsBlock({ newValue, oldValue, isNewBlock: true })}
		</Box>
	);
};
